import { useEffect, useMemo, useState } from "react";
import { Bot, History, MessageSquareText, UserRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AionEmptyState } from "@/components/aion/AionEmptyState";
import { AionSectionCard } from "@/components/aion/AionSectionCard";

const formatConversationTime = (value) => {
  if (!value) return "Zeitpunkt nicht verfügbar";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(parsed);
};

const messageText = (message) => String(
  message?.message
  || message?.text
  || message?.content
  || "",
).trim();

const sessionSearchText = (session) => [
  session?.preview,
  session?.session_id,
  session?.language,
  ...(session?.messages || []).flatMap((message) => [message?.role, messageText(message)]),
].filter(Boolean).join(" ").toLowerCase();

export const ConversationView = ({ activeProfile, conversationHistory = [], onOpenMemory }) => {
  const [query, setQuery] = useState("");
  const readOnlySharedProfile = activeProfile?.access_mode === "shared_view";
  const [selectedSessionId, setSelectedSessionId] = useState(null);

  const filteredSessions = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return conversationHistory;
    return conversationHistory.filter((session) => sessionSearchText(session).includes(needle));
  }, [conversationHistory, query]);

  useEffect(() => {
    if (!filteredSessions.length) {
      setSelectedSessionId(null);
      return;
    }
    if (!filteredSessions.some((session) => session.session_id === selectedSessionId)) {
      setSelectedSessionId(filteredSessions[0]?.session_id || null);
    }
  }, [filteredSessions, selectedSessionId]);

  const selectedSession = useMemo(
    () => filteredSessions.find((session) => session.session_id === selectedSessionId) || null,
    [filteredSessions, selectedSessionId],
  );

  return (
    <div className="aion-view aion-view-stack" data-testid="conversation-view">
      <AionSectionCard
        title="Gespräche"
        eyebrow="CONVERSATION HISTORY"
        subtitle="Deine gespeicherten AION-Sessions mit vollständigem Verlauf, Sprache, Zeitpunkt und Session-Referenz."
        action={!readOnlySharedProfile && onOpenMemory ? <Button size="sm" variant="outline" onClick={() => onOpenMemory()} data-testid="conversation-open-memory">Memory öffnen</Button> : null}
        testId="conversation-hero-card"
      >
        <div className="aion-status-badge" data-testid="conversation-profile-badge">
          <span>Profil: {activeProfile?.name || "Ich"}</span>
          <small>{activeProfile?.access_mode === "shared_view" ? "Freigegebener Gesprächskontext" : "Privater Gesprächsverlauf"}</small>
        </div>
        <div className="aion-search-row" data-testid="conversation-search-row">
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Gespräche durchsuchen"
            data-testid="conversation-search-input"
          />
          <div className="aion-inline-stat" data-testid="conversation-session-count">
            <span>Sessions</span>
            <strong>{filteredSessions.length}</strong>
          </div>
        </div>
      </AionSectionCard>

      <div className="aion-two-column" data-testid="conversation-workspace">
        <AionSectionCard
          title="Verlauf"
          eyebrow="SESSIONS"
          subtitle="Wähle eine frühere Session aus."
          testId="conversation-session-list-card"
        >
          {filteredSessions.length ? (
            <div className="aion-memory-list" data-testid="conversation-session-list">
              {filteredSessions.map((session, index) => {
                const active = session.session_id === selectedSessionId;
                return (
                  <button
                    type="button"
                    key={session.session_id || index}
                    className={`aion-memory-card${active ? " aion-memory-card-active" : ""}`}
                    aria-current={active ? "true" : undefined}
                    onClick={() => setSelectedSessionId(session.session_id)}
                    data-testid={`conversation-session-${index}`}
                  >
                    <strong>{session.preview || `Gespräch ${index + 1}`}</strong>
                    <small>
                      {session.message_count || session.messages?.length || 0} Nachrichten · {session.language || "de"}
                    </small>
                    <small>{formatConversationTime(session.last_active || session.messages?.[0]?.timestamp)}</small>
                  </button>
                );
              })}
            </div>
          ) : (
            <AionEmptyState
              icon={History}
              title={query.trim() ? "Kein passendes Gespräch." : "Noch kein Gesprächsverlauf."}
              description={query.trim() ? "Ändere den Suchbegriff oder öffne eine andere Session." : "Gespeicherte AION-Sessions erscheinen hier automatisch."}
              testId="conversation-session-empty"
            />
          )}
        </AionSectionCard>

        <AionSectionCard
          title={selectedSession?.preview || "Gespräch öffnen"}
          eyebrow="SESSION"
          subtitle={selectedSession ? `${selectedSession.message_count || selectedSession.messages?.length || 0} Nachrichten · ${selectedSession.language || "de"}` : "Wähle links eine Session aus."}
          testId="conversation-detail-card"
        >
          {selectedSession ? (
            <div className="aion-view-stack" data-testid="conversation-detail">
              <div className="aion-status-badge" data-testid="conversation-session-reference">
                <span>{readOnlySharedProfile ? "Freigegebene Session" : `Session: ${selectedSession.session_id}`}</span>
                <small>Letzte Aktivität: {formatConversationTime(selectedSession.last_active)}</small>
              </div>
              {(selectedSession.messages || []).length ? (
                <div className="aion-list" data-testid="conversation-message-list">
                  {selectedSession.messages.map((message, index) => {
                    const role = String(message?.role || "system").toLowerCase();
                    const isAssistant = role === "assistant";
                    const isUser = role === "user";
                    const Icon = isAssistant ? Bot : isUser ? UserRound : MessageSquareText;
                    return (
                      <div
                        className="aion-list-row"
                        key={message?.id || `${selectedSession.session_id}-${index}`}
                        data-testid={`conversation-message-${index}`}
                      >
                        <Icon className="aion-list-icon" aria-hidden="true" />
                        <div className="aion-list-copy">
                          <strong>{isAssistant ? "AION" : isUser ? "Du" : "System"}</strong>
                          <span>{messageText(message) || "Leere Nachricht"}</span>
                          <small>{formatConversationTime(message?.timestamp)} · {message?.language || selectedSession.language || "de"}</small>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <AionEmptyState
                  icon={MessageSquareText}
                  title="Keine Nachrichten in dieser Session."
                  description="Die Session ist vorhanden, enthält aber aktuell keine gespeicherten Nachrichten."
                  testId="conversation-message-empty"
                />
              )}
            </div>
          ) : (
            <AionEmptyState
              icon={MessageSquareText}
              title="Wähle ein Gespräch aus."
              description="Der vollständige Nachrichtenverlauf erscheint hier."
              testId="conversation-detail-empty"
            />
          )}
        </AionSectionCard>
      </div>
    </div>
  );
};
